import _ from 'lodash';

import {
  DOCUMENT_DEFAULT_SCALE,
  DOCUMENT_MIN_SCALE,
  DOCUMENT_SCALE_STEP,
  DocumentExtractionRequestMode,
  DocumentUploadSource,
  RECIPIENT_COPY_LABEL,
} from 'constants/documents';
import { normalizeBooleanStringToBoolean } from 'utils/data';

export const formatFilteredDocumentsByCondition = (documents, filterCondition) =>
  _.flow(
    documents => _.filter(documents, filterCondition),
    filteredDocuments => _.groupBy(filteredDocuments, 'documentTypeId'),
    documentsByTypeIdObject =>
      _.mapValues(documentsByTypeIdObject, documentsByTypeId =>
        _.orderBy(documentsByTypeId, [document => new Date(document.createdDate)], ['desc']),
      ),
  )(documents);

export const findLastCreatedDate = types =>
  _.maxBy(types, ({ createdDate }) => (createdDate ? new Date(createdDate).getTime() : undefined));

// Scale 1 with each page's own /Rotate applied, in pdf page order.
export const getPdfPageBaseSizes = pdf =>
  Promise.all(
    Array.from({ length: pdf.numPages }, (__, index) =>
      pdf.getPage(index + 1).then(page => {
        const { width, height } = page.getViewport({ scale: 1, rotation: page.rotate });

        return { width, height };
      }),
    ),
  );

// The document's loading task owns its pdf.js worker, so a failed load must release it.
export const loadPdfWithBaseSizes = async loadingTask => {
  try {
    const pdf = await loadingTask.promise;
    const pageBaseSizes = await getPdfPageBaseSizes(pdf);

    return { pdf, pageBaseSizes };
  } catch (error) {
    loadingTask.destroy();

    throw error;
  }
};

const getPdfPagesWidth = async ({ pdf, scale, pageBaseSizes }) => {
  const baseSizes = pageBaseSizes ?? (await getPdfPageBaseSizes(pdf));

  return baseSizes.map(({ width }) => width * scale);
};

export const getDocumentInitialScale = async ({ pdf, containerRef, pageBaseSizes = null }) => {
  if (!pdf || !containerRef.current) {
    return DOCUMENT_DEFAULT_SCALE;
  }

  const computeScale = async (scale = DOCUMENT_DEFAULT_SCALE) => {
    if (scale === DOCUMENT_MIN_SCALE) {
      return DOCUMENT_MIN_SCALE;
    }

    const pagesWidth = await getPdfPagesWidth({ pdf, scale, pageBaseSizes });

    const pagesMaxWidth = Math.max(...pagesWidth);
    const { clientWidth: containerWidth } = containerRef.current;
    const overflowRatioX = containerWidth / pagesMaxWidth;

    if (overflowRatioX < 1) {
      const nextScaleToCompute = _.round(scale - DOCUMENT_SCALE_STEP, 1);

      return computeScale(nextScaleToCompute);
    }

    return scale;
  };

  return computeScale();
};

export const checkIsMalwareBlocked = isMalware => isMalware === true;

export const checkIsGenAi = document => {
  if (!document) {
    return false;
  }

  const isGenAi = [document?.extractionRequest?.mode, document?.ExtractionRequest?.mode].includes(
    DocumentExtractionRequestMode.GEN_AI,
  );

  return isGenAi;
};

export const checkIsTitlePortalUpload = uploadSource =>
  uploadSource === DocumentUploadSource.TITLE_PORTAL;

// SkynetAPI adds RecipientCopyOf / RecipientSignerName / CannotBeDeleted to the list response
// metadata, and the documents store keeps metadata keys as they arrive (PascalCase strings).
export const checkIsRecipientCopy = document => !_.isNil(document?.RecipientCopyOf);

export const checkCannotBeDeleted = document =>
  normalizeBooleanStringToBoolean(document?.CannotBeDeleted) === true;

export const checkIsDocumentUndeletable = document =>
  checkCannotBeDeleted(document) || checkIsRecipientCopy(document);

export const getRecipientCopyLabel = signerName =>
  signerName ? `${RECIPIENT_COPY_LABEL}: ${signerName}` : RECIPIENT_COPY_LABEL;

export const getRecipientCopyIds = (documents, masterDocumentId) =>
  Object.values(documents || {})
    .filter(document => document.RecipientCopyOf === masterDocumentId)
    .map(document => document.id);

export const getRecipientCopyMasterIds = documents =>
  _.uniq(
    Object.values(documents || {})
      .filter(checkIsRecipientCopy)
      .map(({ RecipientCopyOf }) => RecipientCopyOf),
  );
